import React from 'react';
import { usePermissions } from '@/hooks/usePermissions';

interface PermissionGateProps {
  /** Single permission required */
  permission?: string;
  /** Any of these permissions grants access */
  anyOf?: string[];
  /** All of these permissions required */
  allOf?: string[];
  /** Content to show when user has permission */
  children: React.ReactNode;
  /** Content to show when user lacks permission (optional) */
  fallback?: React.ReactNode;
}

/**
 * Conditionally render content based on user permissions
 * 
 * @example
 * // Single permission
 * <PermissionGate permission="assets.delete">
 *   <DeleteButton />
 * </PermissionGate>
 * 
 * // Any of these permissions
 * <PermissionGate anyOf={['assets.create', 'assets.update']}>
 *   <EditSection />
 * </PermissionGate>
 * 
 * // All permissions required
 * <PermissionGate allOf={['users.view', 'users.update']}>
 *   <UserManagement />
 * </PermissionGate>
 * 
 * // With fallback
 * <PermissionGate permission="reports.export" fallback={<UpgradePrompt />}>
 *   <ExportButton />
 * </PermissionGate>
 */
export function PermissionGate({
  permission,
  anyOf,
  allOf,
  children,
  fallback = null,
}: PermissionGateProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (anyOf && anyOf.length > 0) {
    hasAccess = hasAnyPermission(...anyOf);
  } else if (allOf && allOf.length > 0) {
    hasAccess = hasAllPermissions(...allOf);
  } else {
    // No permission specified, allow access
    hasAccess = true;
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

/**
 * HOC to wrap a component with permission check
 * 
 * @example
 * const ProtectedButton = withPermission(DeleteButton, 'assets.delete');
 */
export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  permission: string,
  Fallback?: React.ComponentType
) {
  return function PermissionWrapper(props: P) {
    const { hasPermission } = usePermissions();
    
    if (hasPermission(permission)) {
      return <Component {...props} />;
    }
    
    return Fallback ? <Fallback /> : null;
  };
}
